import cn from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Heading, Overlay, Button } from "@rshbintech.dboul/core-ui";
import {
  CertificateRegistryApiTypes,
  bulkSignOrchestratorApi,
} from "@rshbintech.dboul/http";
import {
  ArrowLeft as ArrowLeftIcon,
  Close as CloseIcon,
} from "@rshbintech.dboul/icons";
import { releasePluginObjects } from "@rshbintech.dboul/utils";

import {
  mappingUnsignedDocuments,
  getIsDocumentSigned,
  getStartSigning,
  updateCurrencyInfo,
  hasUnprocessedDocument,
  getIsDocumentValid,
} from "./utils";

import { useCadesPlugin } from "./hooks/use-cades-plugin";
import { useCheckLimits } from "./hooks/use-check-limits";
import { useSigning } from "./hooks/use-signing";

import { DocumentsList } from "./documents-list";
import { SigningWidget } from "./signing-widget";
import { WarningDialog } from "./warning-dialog";

import {
  BatchedDocumentsItemMap,
  MassSigningDocumentsProps,
  SignedDocument,
  DocumentsCurrencyInfo,
  DocumentsInfo,
} from "./types";
import {
  CUSTOM_ERROR_KEYS,
  DOCUMENT_SIGNING_ERROR,
  DOCUMENT_STATUS_DEFAULT,
  RUB,
  RUR,
  SIGNATURE_INFO_DEFAULT,
  SIGNING_REQUEST_INTERVAL_DEFAULT,
  SIGNING_STATUSES,
  DOCUMENTS_IN_BATCH_DEFAULT,
  DOCUMENT_SIGNING_STAGES,
  MASS_TITLE,
  SIGNING_TYPES,
  INIT_DOCUMENTS_INFO,
} from "./constants";
import { SIGNING_STATUSES_ANNOTATIONS } from "./signing-widget/constants";
import styles from "./styles.scss";

export const MassSigningDocuments = ({
  isOpened,
  unsignedDocuments,

  //  пропсы documentsInBatch и signingRequestInterval необходимо прокинуть в массовое из clientSettings
  //  лежат в массиве `signing`:
  //    documentsInBatch         "key": "owb-plt-ms-bso.enable-functionality.bulk-signing",
  //    signingRequestInterval   "key": "owb-plt-ms-bso.ui-settings.task-status-sign-polling-period",
  signingRequestInterval = SIGNING_REQUEST_INTERVAL_DEFAULT,
  documentsInBatch = DOCUMENTS_IN_BATCH_DEFAULT,
  signingType = SIGNING_TYPES.SIGN,
  onClose,
  onError,
}: MassSigningDocumentsProps) => {
  const [taskId, setTaskId] = useState("");
  const [certificate, setCertificate] = useState<
    CertificateRegistryApiTypes.ShortCertInfo | undefined
  >(undefined);

  const [error, setError] = useState("");
  const [status, setStatus] = useState<SIGNING_STATUSES>(
    SIGNING_STATUSES.CHECKING_SIGNING_POSSIBILITY
  );

  // INFO: Делаем маппинг входящего списка неподписанных документов
  mappingUnsignedDocuments(unsignedDocuments);

  const isSigning = useMemo(
    () => signingType === SIGNING_TYPES.SIGN,
    [signingType]
  );

  const [documentsInfo, setDocumentsInfo] =
    useState<DocumentsInfo>(INIT_DOCUMENTS_INFO);

  const [hasToStopProcess, setHasToStopProcess] = useState(false);

  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [isReturnButtonVisible, setIsReturnButtonVisible] = useState(true);
  // Документы для отображения на форме подписания
  const [documentsToView, setDocumentsToView] = useState<SignedDocument[]>([]);

  const [isPluginInit, setIsPluginInit] = useState(false);

  const signingRequestIntervalInMilliseconds = useMemo(
    () => signingRequestInterval * 1000,
    [signingRequestInterval]
  );

  const [hasNotProcessedDocuments, setHasNotProcessedDocuments] =
    useState(true);

  const [signedDocumentsCount, setSignedDocumentsCount] = useState(0);
  const [validDocumentsCount, setValidDocumentsCount] = useState(0);

  const [
    isUnprocessedDocumentsMarkedWithError,
    setIsUnprocessedDocumentsMarkedWithError,
  ] = useState(false);

  useEffect(() => {
    let numberOfSigned = 0;
    let numberOfValid = 0;

    documentsToView.forEach((document) => {
      const isDocumentSigned = getIsDocumentSigned(document);
      const isDocumentValid = getIsDocumentValid(document);

      if (isDocumentValid) {
        numberOfValid++;
      }

      if (isDocumentSigned) {
        numberOfSigned++;
      }
    });
    setValidDocumentsCount(numberOfValid);
    setSignedDocumentsCount(numberOfSigned);

    if (documentsToView.length) {
      setHasNotProcessedDocuments(hasUnprocessedDocument(documentsToView));
    }
  }, [documentsToView]);

  // проверки документов при финише подписания
  useEffect(() => {
    // на случай, если по всем документам вернется ошибка и подписывать окажется нечего:
    if (
      status === SIGNING_STATUSES.SIGNING_COMPLETED &&
      !hasNotProcessedDocuments &&
      !signedDocumentsCount
    ) {
      setError(SIGNING_STATUSES_ANNOTATIONS[signingType].ERROR);
    }

    // если подписание прервется из-за ошибки, помечаем ошибкой неподписанные документы
    if (
      status === SIGNING_STATUSES.SIGNING_COMPLETED &&
      signedDocumentsCount !== documentsToView.length &&
      !isUnprocessedDocumentsMarkedWithError
    ) {
      setIsUnprocessedDocumentsMarkedWithError(true);
      setDocumentsToView((documents) =>
        documents.map((document) => {
          const isDocumentSigned = getIsDocumentSigned(document);

          return isDocumentSigned
            ? document
            : {
                ...document,
                documentStatus: {
                  ...document.documentStatus,
                  errorText: DOCUMENT_SIGNING_ERROR,
                },
                signatureInfo: {
                  ...document.signatureInfo,
                  signingStage: DOCUMENT_SIGNING_STAGES.FINISHED_WITH_ERROR,
                },
              };
        })
      );
    }
  }, [
    status,
    signedDocumentsCount,
    hasNotProcessedDocuments,
    documentsToView,
    isUnprocessedDocumentsMarkedWithError,
  ]);

  const updateDocsToView = useCallback((batched?: BatchedDocumentsItemMap) => {
    if (!batched) {
      return;
    }

    setDocumentsToView((documents) =>
      documents.map((document) =>
        batched[document.id] ? batched[document.id] : document
      )
    );
  }, []);

  /* 
   в хук упакованы 4 этапа подписания:
  1. Получение ККБ
  2. Локальное подписание
  3. Отправка документов на проверку
  4. Проверка статусов
  */
  useSigning({
    status,
    isSigning,
    certificate,
    taskId,
    updateDocsToView,
    hasToStopProcess,
    isOpened,
    error,
    signingRequestIntervalInMilliseconds,
    documentsInBatch,
    unsignedDocuments,
    documentsToView,
    setError,
    setStatus,
    setDocumentsToView,
  });

  // дополняем документы необходимыми полями
  useEffect(() => {
    const newDocuments: SignedDocument[] = [];

    unsignedDocuments.forEach((document) => {
      const item: SignedDocument = {
        ...document,
        signatureInfo: SIGNATURE_INFO_DEFAULT,
        documentStatus: DOCUMENT_STATUS_DEFAULT,
      };

      newDocuments.push(item);
    });

    setDocumentsToView(newDocuments);
  }, [unsignedDocuments]);

  // Останавливаем процесс подписания при получении ошибки неверного пин-кода
  useEffect(() => {
    if (error) {
      setStatus(SIGNING_STATUSES.ERROR);
      setHasToStopProcess(true);
      onError?.(error);

      if (error === CUSTOM_ERROR_KEYS.INVALID_PINCODE) {
        bulkSignOrchestratorApi.api.cancelSignTask(taskId);
      }
    }
  }, [error, taskId]);

  // получение коллбэка, стартующего подписание
  const { startSigning } = getStartSigning(
    taskId,
    setIsReturnButtonVisible,
    setStatus,
    setError,
    setCertificate
  );

  // INFO: Подсчет количества документов для вывода их количества в виджете
  useEffect(() => {
    if (isOpened) {
      const documentsCurrencyInfo = {
        totalDocuments: {} as DocumentsCurrencyInfo,
        validDocuments: {} as DocumentsCurrencyInfo,
        signedDocuments: {} as DocumentsCurrencyInfo,
      };

      documentsToView.forEach((document) => {
        const { currency: documentCurrency, amount } = document;
        const isDocumentValid = getIsDocumentValid(document);
        const isDocumentSigned = getIsDocumentSigned(document);

        const currency = documentCurrency === RUR ? RUB : documentCurrency;

        if (currency) {
          updateCurrencyInfo(
            documentsCurrencyInfo.totalDocuments,
            currency,
            amount
          );

          if (isDocumentValid) {
            updateCurrencyInfo(
              documentsCurrencyInfo.validDocuments,
              currency,
              amount
            );
          }

          if (isDocumentSigned) {
            updateCurrencyInfo(
              documentsCurrencyInfo.signedDocuments,
              currency,
              amount
            );
          }
        }
      });

      setDocumentsInfo(documentsCurrencyInfo);
    }
  }, [isOpened, documentsToView]);

  // INFO: Здесь происходит проверка лимитов на бэкенде.
  // Проверяется возможность подписания полученных в компонент документов.
  useCheckLimits({
    isOpened,
    isPluginInit,
    isSigning,
    unsignedDocuments,
    setError,
    setDocumentsToView,
    setTaskId,
    setCertificate,
    setStatus,
  });

  // инициализация крипто-плагина
  useCadesPlugin({
    isHookInitialized: isOpened,
    setIsPluginInit,
    setError,
  });

  useEffect(() => {
    if (!isOpened) {
      setDocumentsToView([]);
      setTaskId("");
      setCertificate(undefined);
      setStatus(SIGNING_STATUSES.CHECKING_SIGNING_POSSIBILITY);
      setError("");
      setDocumentsInfo(INIT_DOCUMENTS_INFO);
      setHasToStopProcess(false);
      setSignedDocumentsCount(0);
      setValidDocumentsCount(0);
      setIsUnprocessedDocumentsMarkedWithError(false);
      if (isPluginInit) {
        releasePluginObjects();
        setIsPluginInit(false);
      }
    }
  }, [documentsInBatch, isOpened, isPluginInit]);

  const handleOpenWarning = () => {
    setIsWarningDialogOpen(true);
  };

  const handleCloseWarning = () => {
    setIsWarningDialogOpen(false);
  };

  const handleClose = async () => {
    if (status !== SIGNING_STATUSES.IS_SIGNING) {
      // после подписания задание переходит в статус "Завершено"
      // запрос на закрытие уже закрытого задания вернет ошибку
      if (
        status !== SIGNING_STATUSES.SIGNING_COMPLETED &&
        error !== CUSTOM_ERROR_KEYS.INVALID_PINCODE &&
        taskId
      ) {
        try {
          await bulkSignOrchestratorApi.api.cancelSignTask(taskId);
        } catch (error) {}
      }

      onClose();

      return;
    }

    handleOpenWarning();
  };

  return (
    <Overlay fixed opened={isOpened}>
      <div className={styles.container}>
        <div className={styles.title}>
          <Button
            className={cn(styles["return-button"], {
              [styles.hidden]: !isReturnButtonVisible,
            })}
            type="circle"
            htmlType="button"
            onClick={handleClose}
          >
            <ArrowLeftIcon />
          </Button>
          <Heading level={1}>{MASS_TITLE[signingType]}</Heading>
          <Button
            className={styles["close-button"]}
            type="circle"
            htmlType="button"
            onClick={handleClose}
          >
            <CloseIcon />
          </Button>
        </div>
        <div className={styles.content}>
          <div className={styles["documents-info-block"]}>
            <DocumentsList
              taskId={taskId}
              documentsInfo={documentsInfo}
              documents={documentsToView}
              signingType={signingType}
            />
          </div>
          <div>
            <SigningWidget
              status={status}
              error={error}
              signingType={signingType}
              validDocumentsCount={validDocumentsCount}
              signedDocumentsCount={signedDocumentsCount}
              documentsTotalCount={documentsToView.length}
              documentsInfo={documentsInfo}
              signDocuments={startSigning}
              onReturn={handleClose}
            />
          </div>
        </div>
      </div>
      <WarningDialog
        taskId={taskId}
        isOpened={isWarningDialogOpen}
        closeDialog={handleCloseWarning}
        closeOverlay={onClose}
        onError={onError}
      />
    </Overlay>
  );
};
